import { Injectable, signal, computed, inject } from '@angular/core';
import { Usuario, CreateUsuarioDTO, UpdateUsuarioDTO, UserRole, UserLookupResult, AssignExistingUsuarioDTO } from '../models/usuario.model';
import { environment } from '../../../environments/environment';
import { ApiService } from './api.service';
import { SolutionTokenService } from './solution-token.service';

@Injectable({
  providedIn: 'root',
})
export class UsuariosService {
  private api = inject(ApiService);
  private solutionToken = inject(SolutionTokenService);

  private _usuarios = signal<any[]>([]);
  private _loading = signal<boolean>(false);

  readonly usuarios = this._usuarios.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly total = computed(() => this._usuarios().length);

  constructor() {}

  async loadUsuarios(): Promise<void> {
    // El técnico no tiene `equipo:ver`, así que este endpoint le responde 403.
    // Varias pantallas que sí puede abrir lo pedían igual —el listado de
    // unidades, el de activos— y dejaban una petición fallida y un error en
    // consola en cada carga. Los nombres ya los resuelve el backend.
    //
    // Sin capacidades todavía no se corta: al arrancar la sesión llegan un
    // instante después, y cortar ahí dejaría la lista vacía para todos.
    const capacidades = this.solutionToken.capacidades();
    if (capacidades.length > 0 && !capacidades.includes('equipo:ver')) {
      this._usuarios.set([]);
      return;
    }

    this._loading.set(true);
    try {
      const res = await this.api.get<any>(
        `${environment.apiUrl}/maintenance-users`,
        { notify: false }
      );
      let list = Array.isArray(res) ? res : res?.data || [];

      list = list.map((u: any) => ({
        ...u,
        correlativeId: u.correlative ? `${u.correlative}` : `${(u.maintenanceId || u.id)?.slice(0, 8) ?? '?'}`
      }));

      this._usuarios.set(list);
    } catch (e) {
      console.error('Error loading maintenance users', e);
      this._usuarios.set([]);
    } finally {
      this._loading.set(false);
    }
  }

  getById(id: string): any | undefined {
    return this._usuarios().find((u) => u.id === id);
  }

  async create(dto: CreateUsuarioDTO): Promise<any> {
    this._loading.set(true);
    try {
      const newUser = await this.api.post<any>(
        `${environment.apiUrl}/maintenance-users`, dto
      );
      await this.loadUsuarios();
      return newUser;
    } catch (e) {
      console.error('Error creating maintenance user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }

  async update(id: string, dto: UpdateUsuarioDTO): Promise<void> {
    this._loading.set(true);
    try {
      await this.api.put(
        `${environment.apiUrl}/maintenance-users/${id}`, dto
      );
      await this.loadUsuarios();
    } catch (e) {
      console.error('Error updating maintenance user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }

  async delete(id: string): Promise<void> {
    this._loading.set(true);
    try {
      await this.api.delete(
        `${environment.apiUrl}/maintenance-users/${id}`
      );
      await this.loadUsuarios();
    } catch (e) {
      console.error('Error deleting maintenance user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }

  async suspend(id: string): Promise<void> {
    this._loading.set(true);
    try {
      await this.api.post(
        `${environment.apiUrl}/maintenance-users/${id}/suspend`, {}
      );
      await this.loadUsuarios();
    } catch (e) {
      console.error('Error suspending maintenance user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }

  async activate(id: string): Promise<void> {
    this._loading.set(true);
    try {
      // Re-activate a user by updating their status to ACTIVE
      await this.update(id, { estatus: 'ACTIVE' });
    } catch (e) {
      console.error('Error activating maintenance user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }

  async lookupByEmail(email: string): Promise<UserLookupResult> {
    return this.api.post<UserLookupResult>(
      `${environment.apiUrl}/maintenance-users/lookup`,
      { email },
    );
  }

  async assignExisting(dto: AssignExistingUsuarioDTO): Promise<any> {
    this._loading.set(true);
    try {
      const result = await this.api.post<any>(
        `${environment.apiUrl}/maintenance-users`, dto
      );
      await this.loadUsuarios();
      return result;
    } catch (e) {
      console.error('Error assigning existing user', e);
      throw e;
    } finally {
      this._loading.set(false);
    }
  }
}

